import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Briefcase } from 'lucide-react';
import { PROJECTS, IMAGES } from '../data/content';

const FILTERS = ['All', 'Infrastructure', 'Industrial EPC', 'Textile Engineering', 'Welding Automation'];

export default function Projects() {
  const [filter, setFilter] = useState('All');

  const filtered = useMemo(
    () => (filter === 'All' ? PROJECTS : PROJECTS.filter((p) => p.industry === filter)),
    [filter]
  );

  return (
    <div className="bg-white">
      <section className="relative pt-44 pb-20 bg-[#0D1B2A] grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${IMAGES.metro})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-[#0D1B2A]/82" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="overline text-white/80 flex items-center gap-3 mb-6">
            <span className="gold-rule" /> Projects
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            Case studies in <span className="text-[#D4AF37]">execution.</span>
          </h1>
          <p className="mt-8 text-white/75 text-lg max-w-3xl leading-relaxed">
            A selection of our delivered and in-flight projects across India, Africa and the
            Middle East — turnkey, multidisciplinary, on-schedule.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-wrap gap-2 mb-12">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`projects-filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border transition-colors ${
                  filter === f
                    ? 'bg-[#0D1B2A] text-white border-[#0D1B2A]'
                    : 'bg-white text-[#0D1B2A] border-[#0D1B2A]/15 hover:border-[#0D1B2A]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group bg-white border border-[#0D1B2A]/10 card-hover-lift overflow-hidden flex flex-col"
              >
                <div className="h-60 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                    {p.industry}
                    <span className="text-[#0D1B2A]/40">·</span>
                    <span className="text-[#0D1B2A]/60">{p.year}</span>
                  </div>
                  <h3 className="mt-3 font-display font-bold text-xl text-[#0D1B2A] leading-snug">
                    {p.name}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#4B5563]">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={13} /> {p.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase size={13} /> {p.client}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={13} /> {p.status}
                    </span>
                  </div>
                  <p className="mt-5 text-sm text-[#4B5563] leading-relaxed flex-1">{p.scope}</p>
                  <div className="mt-6 pt-5 border-t border-[#0D1B2A]/8 flex items-center justify-between">
                    <div
                      className={`text-[11px] uppercase tracking-[0.2em] font-bold ${
                        p.status === 'Delivered'
                          ? 'text-[#16A34A]'
                          : 'text-[#D4AF37]'
                      }`}
                    >
                      {p.status}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
